/**
 * Condivisione di uno studio della Rete Certificata (pagina pubblica
 * `/rete-certificata`): link profondo `?studio=<id>` che riapre la mappa sulla
 * scheda dello studio, testo con i dati dello studio + link (appunti e WhatsApp).
 * Funzioni pure, senza DOM, cosi' restano testabili.
 */

export const PARAM_STUDIO = 'studio';

export interface AttivitaCondivisibile {
  name: string;
  isCertified: 'SI' | 'NO' | 'PREAUTORIZZATO';
}

export interface StudioCondivisibile {
  id: string;
  businessName: string;
  address?: string;
  phone?: string;
  email?: string;
  certifiedActivities?: AttivitaCondivisibile[];
  isSecondarySede?: boolean;
}

const pulisci = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

/** `https://studi.unasca.it/rete-certificata?studio=<id>` */
export const urlStudio = (origin: string, id: string): string =>
  `${String(origin || '').replace(/\/+$/, '')}/rete-certificata?${PARAM_STUDIO}=${encodeURIComponent(id)}`;

/** Attivita' certificate o preautorizzate, in ordine, senza duplicati. */
export const attivitaCondivisibili = (studio: StudioCondivisibile): string[] => {
  const viste = new Set<string>();
  const out: string[] = [];
  for (const a of studio.certifiedActivities || []) {
    const nome = pulisci(a?.name);
    if (!nome || viste.has(nome)) continue;
    if (a.isCertified !== 'SI' && a.isCertified !== 'PREAUTORIZZATO') continue;
    viste.add(nome);
    out.push(nome);
  }
  return out;
};

/**
 * Testo condiviso: dati dello studio + link alla scheda. Le righe dei campi
 * vuoti vengono omesse.
 */
export const testoStudio = (studio: StudioCondivisibile, url: string): string => {
  const righe: string[] = [];
  const nome = pulisci(studio.businessName) || 'Studio certificato';
  righe.push(`${nome} - Studio certificato UNASCA${studio.isSecondarySede ? ' (sede secondaria)' : ''}`);
  const indirizzo = pulisci(studio.address);
  if (indirizzo) righe.push(`Indirizzo: ${indirizzo}`);
  const telefono = pulisci(studio.phone);
  if (telefono) righe.push(`Telefono: ${telefono}`);
  const email = pulisci(studio.email);
  if (email) righe.push(`Email: ${email}`);
  const attivita = attivitaCondivisibili(studio);
  if (attivita.length) righe.push(`Pratiche: ${attivita.join(', ')}`);
  righe.push('');
  righe.push(`Scheda sulla mappa della Rete Certificata: ${url}`);
  return righe.join('\n');
};

export const whatsappHref = (testo: string): string =>
  `https://wa.me/?text=${encodeURIComponent(testo)}`;

/** Id dello studio dalla query string (`?studio=...`), o null. */
export const studioDaQuery = (search: string): string | null => {
  try {
    const id = new URLSearchParams(search || '').get(PARAM_STUDIO);
    const v = pulisci(id);
    return v || null;
  } catch {
    return null;
  }
};
